import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, Share2, Linkedin, Instagram, Twitter, Mail, Globe, FileText } from "lucide-react";
import { toast } from "sonner";

interface PlatformContent {
  linkedin_post?: string;
  instagram_caption?: string;
  instagram_story?: string[];
  instagram_reels_script?: string;
  x_single?: string;
  x_thread?: string[];
  x_quote?: string;
  email_template?: string;
  newsletter_block?: string;
  website_card?: string;
  case_study?: string;
  slide_bullets?: string;
  one_liner?: string;
  carousel_captions?: string[];
}

interface PlatformTabsProps {
  content: PlatformContent;
  onCopy?: (platform: string) => void;
  onShare?: (platform: string, content: string) => void;
}

const PlatformTabs = ({ content, onCopy, onShare }: PlatformTabsProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard!");
    onCopy?.(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleShare = async (text: string, platform: string) => {
    if (navigator.share) {
      try {
        await navigator.share({ text });
        onShare?.(platform, text);
        toast.success("Shared successfully!");
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          await copyToClipboard(text, platform);
        }
      }
    } else {
      await copyToClipboard(text, platform);
    }
  };

  const platforms = [
    { 
      id: "linkedin", 
      label: "LinkedIn", 
      icon: Linkedin,
      color: "text-[#0077B5]",
      fields: ["linkedin_post"] 
    },
    { 
      id: "instagram", 
      label: "Instagram", 
      icon: Instagram,
      color: "text-[#E4405F]",
      fields: ["instagram_caption", "instagram_story", "instagram_reels_script"] 
    },
    { 
      id: "x", 
      label: "X / Twitter", 
      icon: Twitter,
      color: "text-foreground",
      fields: ["x_single", "x_thread", "x_quote"] 
    },
    { 
      id: "email", 
      label: "Email", 
      icon: Mail,
      color: "text-brand-500",
      fields: ["email_template", "newsletter_block"] 
    },
    { 
      id: "website", 
      label: "Website", 
      icon: Globe,
      color: "text-brand-600",
      fields: ["website_card", "case_study"] 
    },
    { 
      id: "presentation", 
      label: "Presentation", 
      icon: FileText,
      color: "text-orange-500",
      fields: ["slide_bullets", "one_liner", "carousel_captions"] 
    },
  ];

  const availablePlatforms = platforms.filter(p => 
    p.fields.some(field => content[field as keyof PlatformContent])
  );

  const formatFieldLabel = (field: string) => {
    return field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderContent = (field: string, value: string | string[] | undefined) => {
    if (!value) return null;

    if (Array.isArray(value)) {
      return (
        <div className="space-y-3">
          {value.map((item, index) => (
            <div key={index} className="p-4 bg-muted rounded-lg">
              <Badge variant="outline" className="mb-2">
                {field.includes("thread") ? `Tweet ${index + 1}` : `Slide ${index + 1}`}
              </Badge>
              <p className="whitespace-pre-wrap text-sm">{item}</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => copyToClipboard(item, `${field}_${index}`)}
              >
                {copiedField === `${field}_${index}` ? (
                  <><Check className="h-3 w-3 mr-1" /> Copied</>
                ) : (
                  <><Copy className="h-3 w-3 mr-1" /> Copy</>
                )}
              </Button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="p-4 bg-muted rounded-lg">
        <pre className="whitespace-pre-wrap text-sm font-sans">{value}</pre>
      </div>
    );
  };

  if (availablePlatforms.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center text-muted-foreground">
          No platform-specific content available yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs defaultValue={availablePlatforms[0]?.id} className="space-y-4">
      <TabsList className="flex flex-wrap h-auto gap-2 bg-muted/50 p-2">
        {availablePlatforms.map((platform) => {
          const Icon = platform.icon;
          return (
            <TabsTrigger 
              key={platform.id} 
              value={platform.id}
              className="data-[state=active]:bg-background flex items-center gap-2"
            >
              <Icon className={`h-4 w-4 ${platform.color}`} />
              {platform.label}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {availablePlatforms.map((platform) => {
        const Icon = platform.icon;
        const platformFields = platform.fields.filter(
          (field) => content[field as keyof PlatformContent]
        );

        return (
          <TabsContent key={platform.id} value={platform.id} className="space-y-4">
            {platformFields.map((field) => {
              const value = content[field as keyof PlatformContent];
              const textValue = typeof value === "string" ? value : JSON.stringify(value, null, 2);

              return (
                <Card key={field} className="border-brand-100">
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${platform.color}`} />
                      {formatFieldLabel(field)}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleShare(textValue, field)}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(textValue, field)}
                      >
                        {copiedField === field ? (
                          <><Check className="h-4 w-4 mr-2" /> Copied!</>
                        ) : (
                          <><Copy className="h-4 w-4 mr-2" /> Copy</>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {renderContent(field, value)}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        );
      })}
    </Tabs>
  );
};

export default PlatformTabs;
